import React from "react";
import { Tag } from "antd";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../../components/upload/shared/uploadReport";
import { activityExecutionApis, driveChainApis, executionNormApis } from "../../../../api/ActivityExecution";
import { logHoursApis } from "../../../../api/TimeLog";
import { writeValidatedWorkbook } from "./filledWorkbook";
import {
  FilledSheetDownload,
  inBatches,
  messageColumns,
  numberColumn,
  reportMessageColumns,
  reportStatus,
  rowColumn,
  rowErrors,
  sheetDate,
  statusColumn,
} from "./dailyUploadShared";

// Civil upload — one sheet, one row per BOQ line x stretch.
//   Date | Activity ID | Activity Name | Task ID | Task Name | Shift |
//   Start Time | End Time | Class | From CH | To CH | Remarks
// A row with To CH is a stretch driven; rows repeating the same activity, day,
// shift and chainage are one stretch. Each line's quantity is derived from
// the stretch at its norm, so the site types To CH and nothing else. Rows
// without To CH are skipped, so the prefilled day sheet can go back with only
// the faces that were worked. The server splits the sheet and replays the
// drawer's own save per entry, so every rule matches. Older two-sheet files
// (Execution + Actuals) are still accepted.

const HEADERS = [
  "Date", "Activity ID", "Activity Name", "Task ID", "Task Name", "Shift", "Start Time", "End Time",
  "Class", "From CH", "To CH", "Remarks",
];
const WIDTHS = [14, 13, 36, 22, 40, 16, 11, 11, 12, 11, 11, 30];
const COL = { shift: 6, class: 9, toCh: 11, remarks: 12 };
// What the site types: To CH, and a remark when there is one.
const INPUT_COLUMNS = [COL.toCh, COL.remarks];
const DEFAULT_SHIFT = /morning/i;

const INSTRUCTIONS: string[][] = [
  ["Civil daily upload — Instructions"],
  [""],
  ["One row per task", "Each row is one BOQ line (Task ID) on one stretch. The day sheet comes prefilled with every tracked activity and its tracked lines — Morning Shift, the face's From CH and last Class already in. Fill To CH; the tinted columns are the ones to type in."],
  [""],
  ["Date / Activity ID", "Required. Activity ID is the code in the activity name (AE-0100). Activity Name and Task Name are for reading only."],
  ["Shift", "Defaults to Morning Shift. Change it for another shift from the project calendar; blank = the whole-day entry."],
  ["Start / End Time", "From the shift's calendar times. HH:MM; a night shift may end before it starts."],
  ["Class", "The class driven, prefilled with the face's last class. Change it when the rock changed."],
  ["From CH / To CH", "The stretch driven. From CH is where the face stands; fill To CH. Rows of one activity, day and shift with the same chainage are ONE stretch, so repeat it on each task row — copy To CH down the activity's rows. A second stretch (another class): copy the task rows and give them the new chainage."],
  ["Quantities", "Each line's quantity is the stretch length × its norm for the class (1.45 m × 42.298 = 61.3321 Cum) — shown in the upload preview before anything is saved."],
  ["Remarks", "Optional. The entry's remark — on any one of its rows."],
  [""],
  ["Untouched rows", "A row without To CH is skipped — tracked activities not worked that day create nothing."],
  ["Replace rule", "An activity + date + shift with any stretch in the file is replaced in full by the file's stretches. Other entries are untouched. A saved day downloads with its own stretches, so sending it back changes only what you edit."],
  ["Locked days", "A day whose daily progress report was submitted cannot be changed."],
];

const CODE_IN_NAME = /\(([A-Z]{1,4}-\d{2,5})\)/i;
const activityCode = (name: string) => CODE_IN_NAME.exec(name || "")?.[1] || name || "";
const labelKey = (label: string) => String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const hhmm = (time: any) => (time ? String(time).slice(0, 5) : "");

const writeCivilWorkbook = (fileName: string, rows: any[][], classes: string[], shifts: string[]) =>
  writeValidatedWorkbook(
    fileName,
    {
      name: "Civil Execution",
      headers: HEADERS,
      widths: WIDTHS,
      rows,
      inputColumns: INPUT_COLUMNS,
      dropdowns: [
        { column: COL.class, values: classes, error: "Pick a class of the norm in force." },
        { column: COL.shift, values: shifts, error: "Pick a shift from the project calendar." },
      ],
    },
    INSTRUCTIONS
  );

const generateBlankTemplate = () =>
  writeCivilWorkbook(
    "civil-daily-upload-template.xlsx",
    [
      ["15-Sep-2026", "AE-0100", "", "AE-0100/B3.1.1", "", "Morning Shift", "07:00", "19:00", "Class-II", 101, 102.5, "Two rounds"],
      ["15-Sep-2026", "AE-0100", "", "AE-0100/B3.9", "", "Morning Shift", "07:00", "19:00", "Class-II", 101, 102.5, ""],
      ["15-Sep-2026", "AE-0100", "", "AE-0100/B3.1.1", "", "Morning Shift", "07:00", "19:00", "Class-III", 102.5, 104, ""],
      ["15-Sep-2026", "AE-0100", "", "AE-0100/B3.9", "", "Morning Shift", "07:00", "19:00", "Class-III", 102.5, 104, ""],
    ],
    ["Class-I", "Class-II", "Class-III", "Class-IV", "Class-V"],
    ["Morning Shift", "Night Shift"]
  );

// Rows of an entry already saved that day: every line x every stretch it draws
// from. A stretch no line draws from rides on a row without a Task ID, and the
// entry's remark on its first row — so the file carries the whole entry and
// sending it back unchanged changes nothing (recorded actuals stay: a line not
// named keeps its actuals while its stretch is unchanged).
const savedEntryRows = (entry: any, date: string): any[][] => {
  const head = [sheetDate(date), activityCode(entry.activityName), entry.activityName || ""];
  const shift = entry.shiftName || "";
  const times = [hhmm(entry.startTime), hhmm(entry.endTime)];
  const stretches = (entry.slots || []).flatMap((slot: any) =>
    (slot.stretches || []).map((s: any) => ({ slotKey: slot.slotKey, slotLabel: slot.slotLabel, from: Number(s.fromChainage), to: Number(s.toChainage) }))
  );
  const rows: any[][] = [];
  const drawn = new Set<string>();
  (entry.lines || []).forEach((line: any) => {
    const contributions = line.contributions || [];
    stretches
      .filter((s: any) => contributions.some((c: any) => c.slotKey === s.slotKey))
      .forEach((s: any) => {
        drawn.add(`${s.from}|${s.to}`);
        rows.push([...head, line.taskSequenceId, line.taskName || "", shift, ...times, s.slotLabel, s.from, s.to, ""]);
      });
  });
  stretches
    .filter((s: any) => !drawn.has(`${s.from}|${s.to}`))
    .forEach((s: any) => rows.push([...head, "", "", shift, ...times, s.slotLabel, s.from, s.to, ""]));
  if (rows.length && entry.remarks) rows[0][COL.remarks - 1] = entry.remarks;
  return rows;
};

// The day sheet: saved entries as they stand, then every tracked activity not
// yet captured that day with its tracked lines — Morning Shift and its times,
// the face's From CH and last class filled in, To CH left to the site.
const buildDaySheet = (projectId: number | string) => async (date: string): Promise<number> => {
  const [list, activityRes, shiftRes] = await Promise.all([
    activityExecutionApis.list(projectId, { fromDate: date, toDate: date, pageNo: 1, pageSize: 50 }),
    executionNormApis.listActivities(projectId, { asOfDate: date }),
    logHoursApis.getShifts(projectId),
  ]);
  if (list?.status !== true) throw new Error(list?.message || "Could not load the day's entries");
  if (activityRes?.status !== true) throw new Error(activityRes?.message || "Could not load activities");
  const shiftList: any[] = shiftRes?.data?.response || shiftRes?.data || [];
  const shifts = Array.isArray(shiftList) ? shiftList : [];
  const morning = shifts.find((s) => DEFAULT_SHIFT.test(s.shiftName || "")) || shifts[0] || null;

  const entries = (list.data?.dates || []).flatMap((d: any) => (d.phases || []).flatMap((phase: any) => phase.entries || []));
  const details = (
    await inBatches(entries, 4, (entry: any) =>
      activityExecutionApis
        .getEntry({ entryId: entry.entryId })
        .then((res: any) => (res?.status === true ? res.data : null))
        .catch(() => null)
    )
  ).filter(Boolean);
  const captured = new Set(details.map((entry: any) => Number(entry.activityId)));
  const classes = new Set<string>();
  const rows: any[][] = [];
  details.forEach((entry: any) => {
    (entry.slots || []).forEach((slot: any) => slot.slotLabel && classes.add(slot.slotLabel));
    rows.push(...savedEntryRows(entry, date));
  });

  const tracked = ((activityRes.data?.activities || []) as any[]).filter(
    (activity) => Number(activity.trackedTaskCount) > 0 && !captured.has(Number(activity.activityId))
  );
  const fresh = await inBatches(tracked, 4, async (activity: any) => {
    // The version in force on the date comes with the entry lookup — the same
    // call the drawer makes before it loads norms.
    const probe = await activityExecutionApis
      .getEntry({ activityId: activity.activityId, executionDate: date, ...(morning ? { shiftId: morning.shiftId } : {}) })
      .catch(() => null);
    const versionId = Number(probe?.data?.normVersionId) || 0;
    if (probe?.status !== true || probe.data?.normsInForce === false || !versionId) return [];
    const [normRes, chainRes] = await Promise.all([
      executionNormApis.getByActivity(activity.activityId, versionId, date).catch(() => null),
      driveChainApis.get(activity.activityId).catch(() => null),
    ]);
    if (normRes?.status !== true) return [];
    const slots: any[] = (normRes.data?.slots || []).filter((slot: any) => slot.isActive !== false);
    slots.forEach((slot) => slot.slotLabel && classes.add(slot.slotLabel));
    const tasks: any[] = (normRes.data?.tasks || []).filter((task: any) => task.isMonitored === true);
    // Where the face stands — only when nothing was driven on or after the
    // date, since the ledger answers for today, not for a back-dated day.
    const chain = chainRes?.status === true ? chainRes.data : null;
    const reaches: any[] = chain?.reaches || [];
    const ahead =
      String(chain?.lastCaptureDate || "") >= date || reaches.some((reach) => String(reach.lastDate || "") >= date);
    const face = !ahead && chain?.faceChainage !== "" && chain?.faceChainage != null ? Number(chain.faceChainage) : "";
    const lastClass = !ahead && reaches.length ? reaches[reaches.length - 1].classLabel : "";
    const slot = slots.find((s) => labelKey(s.slotLabel) === labelKey(lastClass));
    const name = activity.activityName || "";
    return tasks.map((task) => [
      sheetDate(date), activityCode(name), name, task.taskSequenceId, task.taskName || "",
      morning?.shiftName || "", hhmm(morning?.shiftStartTime), hhmm(morning?.shiftEndTime),
      slot?.slotLabel || "", Number.isFinite(face as number) ? face : "", "", "",
    ]);
  });
  fresh.forEach((block) => rows.push(...block));

  await writeCivilWorkbook(
    `civil-day-sheet-${date}.xlsx`,
    rows,
    Array.from(classes),
    shifts.map((s) => s.shiftName).filter(Boolean)
  );
  return rows.length;
};

const textColumn = (title: string, dataIndex: string, width: number) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  ellipsis: true,
  render: (value: any) => (value === "" || value == null ? <span style={{ color: "#8094ae" }}>—</span> : value),
});

const previewColumns: any[] = [
  statusColumn,
  {
    title: "Sheet",
    dataIndex: "Sheet",
    key: "Sheet",
    width: 96,
    fixed: "left",
    // The server keeps the two-sheet names; on the one-sheet file a row is a
    // stretch driven or a line's actual.
    render: (value: string) => (
      <Tag color={value === "Actuals" ? "purple" : "geekblue"}>{value === "Actuals" ? "Actual" : "Stretch"}</Tag>
    ),
  },
  rowColumn,
  {
    title: "Activity",
    key: "activity",
    width: 220,
    ellipsis: true,
    render: (_: any, row: any) => row.activityName || row.Activity,
  },
  textColumn("Date", "Date", 104),
  textColumn("Shift", "Shift", 120),
  textColumn("Class", "Class", 96),
  {
    title: "Task",
    key: "task",
    width: 220,
    ellipsis: true,
    render: (_: any, row: any) =>
      row.TaskID ? (
        <span>
          <b>{row.TaskID}</b> {row.taskName}
        </span>
      ) : (
        <span style={{ color: "#8094ae" }}>—</span>
      ),
  },
  numberColumn("From CH", "FromCH", 90),
  numberColumn("To CH", "ToCH", 90),
  numberColumn("Length m", "Length", 90),
  numberColumn("Norm", "Norm", 96),
  numberColumn("Value", "Value", 100),
  numberColumn("Target", "Target", 100),
  numberColumn("Lines", "Lines", 70),
  textColumn("Remarks", "Remarks", 200),
  ...messageColumns,
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errors = rows.filter((row) => rowErrors(row).length);
  const warnings = rows.filter((row) => !rowErrors(row).length && row?.status === "Warning");
  const execution = rows.filter((row) => row?.Sheet === "Execution");
  const actuals = rows.filter((row) => row?.Sheet === "Actuals");
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "execution", label: `Stretches (${execution.length})`, rows: execution },
    { key: "actuals", label: `Actuals (${actuals.length})`, rows: actuals },
    { key: "warnings", label: `Warnings (${warnings.length})`, rows: warnings },
    { key: "errors", label: `Errors (${errors.length})`, rows: errors },
  ];
};

const entryCount = (report: UploadReportState) =>
  new Set(report.rows.map((row: any) => `${row.activityName || row.Activity}|${row.Date}|${row.Shift}`)).size;

const CivilUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Civil — Excel upload",
      entityLabel: "Civil execution",
      templateSource: { kind: "client", generate: generateBlankTemplate },
      introSection: (
        <FilledSheetDownload
          label="Or download the day sheet, prefilled"
          hint="Every tracked activity with its tracked BOQ lines — Morning Shift, the face's From CH and last Class filled in — plus anything already saved that day. Fill To CH and upload it back."
          build={buildDaySheet(projectId)}
        />
      ),
      startUpload: (formData) => activityExecutionApis.uploadDailyExcel(formData),
      buildBody: (comments) => ({ projectId: String(projectId), comment: comments ? comments : "N/A" }),
      previewColumns,
      previewScrollX: 2600,
      previewPageSize: 100,
      bucketRows,
      summaryTags: (report: UploadReportState) =>
        report.rows.length ? <Tag color="processing">{entryCount(report)} entr{entryCount(report) === 1 ? "y" : "ies"}</Tag> : null,
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "civil-upload-report.xlsx",
        columns: [
          { header: "Status", width: 12, value: reportStatus },
          { header: "Sheet", width: 12, value: (row: any) => row.Sheet || "" },
          { header: "Row", width: 8, value: (row: any) => row.Row ?? "" },
          { header: "Activity", width: 36, value: (row: any) => row.activityName || row.Activity || "" },
          { header: "Date", width: 12, value: (row: any) => row.Date || "" },
          { header: "Shift", width: 16, value: (row: any) => row.Shift || "" },
          { header: "Class", width: 12, value: (row: any) => row.Class || "" },
          { header: "Task ID", width: 22, value: (row: any) => row.TaskID || "" },
          { header: "From CH", width: 10, value: (row: any) => row.FromCH ?? "" },
          { header: "To CH", width: 10, value: (row: any) => row.ToCH ?? "" },
          { header: "Value", width: 12, value: (row: any) => row.Value ?? "" },
          { header: "Target", width: 12, value: (row: any) => row.Target ?? "" },
          ...reportMessageColumns,
        ],
      },
      rowErrorLabel: (row: any) => `${row.Sheet || "Row"} row ${row.Row ?? ""}`.trim(),
      copy: {
        uploadIntro: (
          <p>
            One sheet, one row per BOQ line and stretch. Fill <b>To CH</b> for the stretch driven — each line's
            quantity comes from its norm. An activity, date and shift with a stretch in the file is <b>replaced in
            full</b>; rows without To CH are skipped. You'll see every row checked — with targets
            and variances — before anything is saved.
          </p>
        ),
        validatingHeading: "Checking your file…",
        validatingSub:
          "Matching activities, classes and BOQ lines, deriving each entry's targets from the norms in force, and running the same checks the Add Execution drawer runs.",
        committingHeading: "Saving Civil execution…",
        committingSub: "Saving every entry in one go — if anything fails, nothing is saved.",
        commitCta: "Save Civil execution",
        doneHeading: "Civil execution saved",
        doneSub: (report: UploadReportState) => (
          <>
            {entryCount(report)} entr{entryCount(report) === 1 ? "y" : "ies"} saved from {report.rows.length} row(s). The
            register, task actuals and progress now reflect the file.
          </>
        ),
        creationFailedNote: "Nothing was saved — see the reason below.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId]
  );

  return (
    <ExcelUploadStageModal config={config} open={open} onClose={onClose} onSuccess={onSuccess} projectId={projectId} />
  );
};

export default CivilUploadModal;
